import React from "react";
import { createTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

import useLocalStorage from "lib/localStorage";

export default function createLapidouTheme(mode = "light") {
  const palette = {
    mode,
    background: {
      default: mode === "dark" ? "#1e1e1e" : "#fafafa",
      paper: mode === "dark" ? "#252526" : "#fff",
      odd: mode === "dark" ? "#333333" : "#e3e3e3",
      even: mode === "dark" ? "#444444" : "#f2f2f2",
    },

    ...(mode === "dark" && {
      text: {
        primary: "rgba(255, 255, 255, 0.85)",
        secondary: "rgba(255, 255, 255, 0.7)",
        icon: "rgba(255, 255, 255, 0.5)",
        disabled: "rgba(255, 255, 255, 0.5)",
      },
    }),
  };

  return createTheme({
    palette,
    typography: {
      fontSize: 14,
      htmlFontSize: 14,
      body1: { fontSize: 14, lineHeight: 1.3 },
      body2: { fontSize: 14, lineHeight: 1.3 },
      h1: { fontWeight: 700, fontSize: "2rem", lineHeight: 1.3 },
      h2: { fontWeight: 700, fontSize: "1.8rem", lineHeight: 1.3 },
      h3: { fontWeight: 700, fontSize: "1.3rem", lineHeight: 1.3 },
      h4: { fontWeight: 700, fontSize: "1rem", lineHeight: 1.3 },
      h5: { fontWeight: 700, fontSize: "1rem", lineHeight: 1.3 },
      h6: { fontWeight: 700, fontSize: "1rem", lineHeight: 1.3 },
    },
    components: {
      MuiButton: {
        defaultProps: {
          variant: "contained",
          color: "primary",
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: palette.background.default,
          },
          body: {
            overflow: "hidden",
          },
          ":root": {
            colorScheme: mode,
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          disableRestoreFocus: true,
          fullWidth: true,
          maxWidth: "md",
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          color: "primary",
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: "standard",
          fullWidth: true,
        },
      },
      MuiGrid: {
        defaultProps: {
          spacing: 2,
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 2,
        },
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "standard",
          fullWidth: true,
        },
      },
    },
  });
}

export function useThemeMode() {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  const [mode, setMode] = useLocalStorage(
    "lapidou::theme::mode",
    prefersDarkMode ? "dark" : "light"
  );

  const toggleMode = React.useCallback(
    () => setMode((m) => (m === "light" ? "dark" : "light")),
    [setMode]
  );

  return [mode, toggleMode];
}
